import {
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Project,
  Survey,
  SurveyState
} from '../../../../data/gql-gen/dashboard/graphql'
import { SurveyIdProvider } from '../../../../hooks/SurveyIdContext'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { useBaseMarketLabel } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'
import { useMarketsTranslationWarnings } from '../../../../modules/Translations/hooks/useMarketsTranslationWarnings'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import {
  useMarketsCompletes,
  useMasterQuestionsCount
} from '../ProjectPage/ProjectPage.hooks'
import { getMarkets, surveysByCreation } from '../markets.utils'
import { MarketRow } from './MarketRow'

// Market | Language | Questions | Target | Soft launch | Status | Preview | Action.
// The header and every MarketRow share this template so columns line up.
// Soft launch fits a toggle + completes input; Action fits up to three
// buttons (Full launch / Pause / End) on a live soft-launched market.
const MARKET_GRID_COLUMNS = '1.6fr 1.2fr 0.8fr 0.8fr 1.5fr 0.9fr 0.9fr 1.7fr'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  warningPanel: {
    border: '1px solid #F0C24B',
    background: '#FEF9E7',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20
  },
  warningTitle: { color: '#A86B00' },
  warningItem: { color: '#A86B00', marginTop: 8 },
  panel: { border: '1px solid #E5EAF5', borderRadius: 12, padding: 20 },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: MARKET_GRID_COLUMNS,
    padding: '12px 0',
    marginTop: 12,
    borderBottom: '1px solid #E5EAF5',
    color: '#6B7280'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: MARKET_GRID_COLUMNS,
    padding: '14px 0',
    borderBottom: '1px solid #F1F3F7',
    alignItems: 'center'
  },
  // Cap the rows area so a long list of markets scrolls within the panel
  // instead of growing the page; the column header above stays fixed.
  tableBody: {
    maxHeight: 'calc(100vh - 340px)',
    overflowY: 'auto'
  },
  emptyMarkets: { padding: '20px 0', color: '#6B7280' }
})

interface ConfirmPageProps {
  breadcrumb?: ReactNode
  markets?: Survey[]
  onMarketsChanged?: () => unknown
}

// A wave owns its markets. Newly duplicated waves may not yet be present in
// the cached project, so use the supplied scope directly, oldest/master first.
const marketsInScope = (project?: Project, scope?: Survey[]): Survey[] =>
  scope === undefined ? getMarkets(project) : surveysByCreation(scope)

/**
 * The Confirm step of the multi-market flow: a per-market launch dashboard.
 * Each row is a market (a Survey under the project, the base/master included) with
 * its language(s), the shared master question count, target completes, a soft-
 * launch toggle, status and a launch/pause action. Markets with incomplete
 * translations are surfaced in a banner and can't be launched.
 */
const ConfirmPageContent = ({
  breadcrumb,
  markets: scope,
  onMarketsChanged
}: ConfirmPageProps) => {
  const { classes } = useStyles()
  const projectId = useProjectId()
  const { project, loading, refetch } = useGetProjectByProjectId()
  const baseMarket = useBaseMarketLabel()

  const markets = marketsInScope(project, scope)
  // Until the first market launches, the launch dialog carries a "this locks
  // the questionnaire" warning so users lock in all markets beforehand.
  const projectInProgress = markets.some(
    (market) => market.state !== SurveyState.Draft
  )
  const surveyIds = markets.map((survey) => survey.surveyId)
  const {
    completesBySurveyId,
    languagesBySurveyId,
    countryBySurveyId,
    panelSupplierBySurveyId
  } = useMarketsCompletes(surveyIds)
  // The questionnaire is shared, so this column shows its total. Per-market
  // visibility is not applied here (the translation warnings below do apply it).
  const masterSurveyId = markets.at(0)?.surveyId
  const questionsCount = useMasterQuestionsCount(projectId, masterSurveyId)
  const { warnings, incompleteSurveyIds } = useMarketsTranslationWarnings(
    projectId,
    masterSurveyId,
    markets,
    languagesBySurveyId,
    countryBySurveyId
  )

  const handleSurveyChanged = async () => {
    await refetch()
    await onMarketsChanged?.()
  }

  return (
    <div>
      {breadcrumb}

      <div className={classes.page}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          {loading ? 'Loading…' : 'Confirm'}
        </Text>

        {warnings.length > 0 && (
          <div className={classes.warningPanel}>
            <Text size={TextSize.l} weight={TextWeight.Bold}>
              <span className={classes.warningTitle}>Translation Warnings</span>
            </Text>
            {warnings.map((warning) => (
              <div
                key={`${warning.surveyId}-${warning.languageCode}`}
                className={classes.warningItem}
              >
                <Text size={TextSize.ms}>
                  ⚠ {warning.country}/{warning.language}:{' '}
                  {warning.untranslated} untranslated
                </Text>
              </div>
            ))}
          </div>
        )}

        <div className={classes.panel}>
          <div className={classes.tableHead}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              MARKET
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              LANGUAGE
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              QUESTIONS
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              TARGET
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              SOFT LAUNCH
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              STATUS
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              PREVIEW
            </Text>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              ACTION
            </Text>
          </div>

          {markets.length === 0 ? (
            <div className={classes.emptyMarkets}>
              <Text size={TextSize.ms}>
                No markets yet. Add a market on the Audiences step to confirm it
                here.
              </Text>
            </div>
          ) : (
            <div className={classes.tableBody}>
              {markets.map((survey) => (
                <MarketRow
                  key={survey.surveyId}
                  className={classes.tableRow}
                  projectId={projectId}
                  survey={survey}
                  displayName={
                    baseMarket && survey.surveyId === baseMarket.surveyId
                      ? baseMarket.label
                      : undefined
                  }
                  questionsCount={questionsCount}
                  target={completesBySurveyId.get(survey.surveyId)?.target ?? 0}
                  languages={languagesBySurveyId.get(survey.surveyId) ?? []}
                  panelSupplier={panelSupplierBySurveyId.get(survey.surveyId)}
                  hasIncompleteTranslations={incompleteSurveyIds.has(
                    survey.surveyId
                  )}
                  isFirstLaunch={!projectInProgress}
                  onSurveyChanged={handleSurveyChanged}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Confirm operates on the shared project questionnaire. The route is
 * project-scoped, so we derive the base/master survey and expose it via
 * SurveyIdProvider for the subtree's non-data useSurveyId() call sites; the
 * QuestionnaireContext reads the same shared questionnaire the Translations
 * editor does, keeping the translation warnings in sync with it.
 *
 * A tracker wave passes its own `markets` and step chrome, since each wave has
 * its own questionnaire.
 */
export const ConfirmPage = ({
  breadcrumb = <ProjectFlowBreadcrumb activeStep="Confirm" />,
  markets,
  onMarketsChanged
}: ConfirmPageProps) => {
  const projectId = useProjectId()
  const { project, loading } = useGetProjectByProjectId()
  const masterSurveyId = marketsInScope(project, markets).at(0)?.surveyId

  // The content's hooks read useSurveyId(), so it must not mount until the
  // provider has a value — on a hard refresh the project is still loading
  // here (same gate as MasterSurveyPage).
  if (loading) {
    return null
  }
  if (!masterSurveyId) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  return (
    <SurveyIdProvider value={masterSurveyId}>
      <QuestionnaireContextProvider>
        <ConfirmPageContent
          breadcrumb={breadcrumb}
          markets={markets}
          onMarketsChanged={onMarketsChanged}
        />
      </QuestionnaireContextProvider>
    </SurveyIdProvider>
  )
}
